import type { ModalProps } from '@components/modal';
import { atom, getDefaultStore } from 'jotai';
import type { JSXElementConstructor } from 'react';

export type ModalsConfigProps = {
  isOpen: boolean;
  component: JSXElementConstructor<Record<string, unknown>>;
  componentProps: Record<string, unknown> & { onClose: () => void };
  modalProps: Partial<ModalProps>;
};

export const modalsAtom = atom<Record<string, ModalsConfigProps>>({});

const store = getDefaultStore();

// Close interceptors are kept outside the atom because they're a UI-only concern
// (consumed only by ModalContainer at close time). Storing them in the atom would
// force every consumer to re-render whenever an interceptor is registered.
const closeInterceptors = new Map<string, (proceed: () => void) => void>();

export const showModal = async (
  component: ModalsConfigProps['component'],
  modalKey: string,
  onModalOpen?: () => void | Promise<void>,
  componentProps?: ModalsConfigProps['componentProps'],
  modalProps?: Partial<ModalProps>,
) => {
  store.set(modalsAtom, (prev) => ({
    ...prev,
    [modalKey]: {
      isOpen: true,
      component,
      componentProps: componentProps ?? ({} as ModalsConfigProps['componentProps']),
      modalProps: modalProps ?? {},
    },
  }));
  if (onModalOpen) {
    await onModalOpen();
  }
};

export const hideModal = (modalKey: string) => {
  store.set(modalsAtom, (prev) => ({
    ...prev,
    [modalKey]: { ...prev[modalKey], isOpen: false },
  }));
  closeInterceptors.delete(modalKey);
};

export const registerCloseInterceptor = (
  modalKey: string,
  interceptor: (proceed: () => void) => void,
) => {
  closeInterceptors.set(modalKey, interceptor);
};

export const getCloseInterceptor = (modalKey: string) => closeInterceptors.get(modalKey);
